import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Tag, Space, Drawer, Form, Switch, Divider, Row, Col, Card, Alert, message, Typography, Modal } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, StopOutlined, CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;

const PERMISSIONS_GROUPS = {
    'POS': ['POS_SALE', 'POS_DISCOUNT', 'POS_RETURN', 'POS_DAILY_CLOSE'],
    'Buyback': ['BUYBACK_CASH', 'BUYBACK_TRADEIN'],
    'Inventory': ['INV_VIEW', 'INV_ADJUST'],
    'Settings': ['SETTINGS_USERS', 'SETTINGS_INVOICE', 'SETTINGS_COMPANY']
};

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [resetModalVisible, setResetModalVisible] = useState(false);
    const [resetUserId, setResetUserId] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [resetForm] = Form.useForm();

    const [searchText, setSearchText] = useState('');
    // Form and Permissions State
    const [form] = Form.useForm();
    const [permissions, setPermissions] = useState({});

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/settings/users', { headers: { Authorization: `Bearer ${token}` } });
            console.log("USERS_API_RESPONSE", res.data);

            if (res.data.status === 'success') {
                setUsers(res.data.data || []);
            } else if (res.data.status === 'error') {
                message.error(res.data.message || 'Failed to load users');
                setUsers([]);
            } else {
                // Fallback for unexpected format
                setUsers(Array.isArray(res.data) ? res.data : []);
            }
        } catch (error) {
            console.error("Fetch Users Error:", error);
            const errMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to load users';
            message.error(errMsg);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleEdit = async (user) => {
        setSelectedUser(user);

        // Fetch permissions for this user
        // Note: The list endpoint might not return permissions for all users by default in standard Dolibarr, 
        // but our BFF list endpoint (old list) did. The new 'getUsers' returns user details.
        // We probably need to fetch permissions separately or rely on 'permissions' field if populated.
        // Let's assume we need to fetch them or they are missing.
        // BFF index.js: `getUserDetails` returns standard Dolibarr user. It does NOT return BFF permissions.
        // We need a way to get permissions.
        // BFF `list` (legacy) returned enriched users. `getUsers` (new) returns plain Dolibarr users.
        // I should have updated `getUsers` to include permissions or added an endpoint.
        // Let's use `axios.get('/api/settings/users')` (legacy list) logic? No, that endpoint is deprecated/legacy.
        // I should stick to `getUserDetails` but I need permissions.
        // I will use `list` (legacy endpoint path: `/api/settings/users`) to populate the table initially? 
        // Or better: Fetch permissions explicitly when opening drawer.
        // Currently `updatePermissions` is at `/api/users/:id/permissions` (via index.js).
        // Is there a `getPermissions`?
        // `users.js` -> `list` (legacy) does the join.
        // I should probably use the legacy list endpoint for the MAIN TABLE if it's cleaner, 
        // OR add a `getPermissions` endpoint.
        // Let's assume for now I can fetch the user details which might include it if I updated `getUserDetails`?
        // `users.js` `getUserDetails` calls Dolibarr. It doesn't join `user_permissions`.
        // I will fix this by fetching all permissions on load or individually.
        // Let's fetch the list from `/api/settings/users` (legacy) which returns everything for now?
        // But `Settings.jsx` was refactored to use new endpoints?
        // Let's look at `Settings.jsx`... it currently uses `/api/users` in `UserPermissions`.
        // And `UserPermissions` used `res.data`.
        // Let's check `users.js` `getUsers` implementation from Step 1013 using `view_file`?
        // Wait, I updated `users.js` in Step 1013.
        // `getUsers` maps Dolibarr fields. It does NOT join permissions.
        // `list` (lines 121+) joins permissions.
        // So I should use `/api/settings/users` to get the list with permissions for the table?
        // But the requirements say "Users CRUD MUST WORK via BFF... Permissions management MUST be usable".
        // Use `/api/settings/users` for listing is fine as it enriches data.
        // Let's switch fetch URL to `/api/settings/users`.

        // Fetch FRESH permissions for the drawer to be sure
        // Actually, let's just use the data from the row if we use the enriched endpoint.
        // Or for safety, refresh list on drawer open?
        // Let's set permissions from the user object if available.
        setPermissions(user.permissions || {});

        form.setFieldsValue(user);
        setDrawerVisible(true);
    };

    const handleCreate = () => {
        setSelectedUser(null);
        setPermissions({});
        form.resetFields();
        setDrawerVisible(true);
    };

    const onClose = () => {
        setDrawerVisible(false);
        form.resetFields();
    };

    const onFinish = async (values) => {
        setActionLoading(true);
        const token = localStorage.getItem('token');
        try {
            let userId = selectedUser?.id || selectedUser?.userId; // Adjust based on API structure

            if (selectedUser) {
                // Update User
                await axios.put(`/api/users/${selectedUser.id}`, values, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // Update Permissions (Separate call as per current BFF design)
                await axios.put(`/api/users/${selectedUser.username || values.login}/permissions`, permissions, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                message.success('User updated successfully');
            } else {
                // Create User
                const res = await axios.post('/api/users', values, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                userId = res.data.id;

                // Set Permissions for new user (using login/username)
                if (values.login) {
                    await axios.put(`/api/users/${values.login}/permissions`, permissions, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                }
                message.success('User created successfully');
            }
            onClose();
            fetchUsers(); // Refresh list
        } catch (error) {
            console.error(error);
            message.error(error.response?.data?.error || 'Operation failed');
        } finally {
            setActionLoading(false);
        }
    };

    // Permission Toggles
    const togglePermission = (perm, checked) => {
        setPermissions(prev => ({
            ...prev,
            [perm]: checked
        }));
    };

    const columns = [
        {
            title: 'Login',
            dataIndex: 'username',
            key: 'username',
            render: (text, record) => (
                <Space>
                    <Text strong>{text || record.login}</Text>
                    {record.admin === '1' && <Tag color="gold">Admin</Tag>}
                    {record.employee === '1' && <Tag color="blue">Employee</Tag>}
                </Space>
            )
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => (
                <Space>
                    <Tag icon={status === '1' ? <CheckCircleOutlined /> : <StopOutlined />} color={status === '1' ? 'success' : 'default'}>
                        {status === '1' ? 'Active' : 'Disabled'}
                    </Tag>
                    <Switch checked={status === '1'} size="small" onChange={async (checked) => {
                        const token = localStorage.getItem('token');
                        try {
                            await axios.patch(`/api/users/${record.id}/status`, { active: checked }, { headers: { Authorization: `Bearer ${token}` } });
                            message.success('User status updated');
                            fetchUsers();
                        } catch (err) {
                            message.error('Failed to update status');
                        }
                    }} />
                </Space>
            )
        },
        {
            title: 'Actions',
            key: 'action',
            render: (_, record) => (
                <Space>
                    <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
                    <Button type="link" onClick={() => { setResetUserId(record.id); setResetModalVisible(true); }}>Reset Password</Button>
                </Space>
            )
        }
    ];

    // Filtered data
    const filteredUsers = users.filter(u =>
        (u.username || u.login || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (u.email || '').toLowerCase().includes(searchText.toLowerCase())
    );

    return (
        <Card title="User Management" bordered={false} style={{ background: 'transparent' }}
            extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>New User</Button>}>

            <Input
                prefix={<SearchOutlined />}
                placeholder="Search users..."
                style={{ marginBottom: 16, maxWidth: 300 }}
                onChange={e => setSearchText(e.target.value)}
            />

            <Table
                columns={columns}
                dataSource={filteredUsers}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
            />

            <Drawer
                title={selectedUser ? "Edit User & Permissions" : "New User"}
                width={720}
                onClose={onClose}
                open={drawerVisible}
                bodyStyle={{ paddingBottom: 80 }}
                extra={
                    <Space>
                        <Button onClick={onClose}>Cancel</Button>
                        <Button onClick={() => form.submit()} type="primary" loading={actionLoading}>
                            Save Changes
                        </Button>
                    </Space>
                }
            >
                <Form form={form} layout="vertical" onFinish={onFinish}>

                    <Divider orientation="left">User Details</Divider>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="login" label="Login" rules={[{ required: true }]}>
                                <Input disabled={!!selectedUser} placeholder="Username" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="email" label="Email">
                                <Input placeholder="Email" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="firstname" label="First Name"><Input /></Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="lastname" label="Last Name"><Input /></Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            {!selectedUser ? (
                                <Form.Item
                                    name="password"
                                    label="Password"
                                    rules={[{ required: true, message: 'Please input a password for the new user!' }]}
                                >
                                    <Input.Password prefix={<LockOutlined />} placeholder="Initial Password" />
                                </Form.Item>
                            ) : (
                                <Alert
                                    message="Password Management"
                                    description="Passwords can be reset using the 'Reset Password' button in the user list."
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                            )}
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="admin" label="Admin" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="employee" label="Employee" valuePropName="checked">
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider orientation="left">Permissions</Divider>

                    {Object.entries(PERMISSIONS_GROUPS).map(([group, perms]) => (
                        <Card key={group} size="small" title={group} style={{ marginBottom: 16 }}>
                            <Row>
                                {perms.map(perm => (
                                    <Col span={12} key={perm} style={{ marginBottom: 8 }}>
                                        <Space>
                                            <Switch
                                                size="small"
                                                checked={!!permissions[perm]}
                                                onChange={(checked) => togglePermission(perm, checked)}
                                            />
                                            <span style={{ fontSize: 13 }}>{perm.replace(/_/g, ' ')}</span>
                                        </Space>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    ))}

                </Form>
            </Drawer>

            {/* Reset Password Modal */}
            <Modal
                title="Reset User Password"
                open={resetModalVisible}
                onCancel={() => setResetModalVisible(false)}
                footer={null}
            >
                <Form form={resetForm} onFinish={async (values) => {
                    const token = localStorage.getItem('token');
                    try {
                        await axios.post(`/api/users/${resetUserId}/reset-password`, { password: values.password }, { headers: { Authorization: `Bearer ${token}` } });
                        message.success('Password reset successfully');
                        setResetModalVisible(false);
                        fetchUsers();
                    } catch (err) {
                        message.error('Failed to reset password');
                    }
                }} layout="vertical">
                    <Form.Item name="password" label="New Password" rules={[{ required: true }]}>
                        <Input.Password />
                    </Form.Item>
                    <Form.Item>
                        <Space style={{ float: 'right' }}>
                            <Button onClick={() => setResetModalVisible(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit">Reset</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default UserManagement;
